import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { Clock } from 'lucide-react';
import { PostureChip, type PostureStatus } from '@/components/ui/posture-chip';
import { Sparkline } from './sparkline';
import { TeamAvatars, type TeamMember } from '@/components/clients/team-avatars';
import { rollupColorToPostureStatus } from '@/lib/api/health-posture';
import type {
  EngagementDetailDto,
  PortfolioEngagementDto,
  ReadinessTrendResult,
} from '@/lib/api/portfolio';
import { formatDate } from '@/lib/utils/format';
import { cn } from '@/lib/utils/cn';

/**
 * Portfolio engagements table (T-0107 / C20-130).
 *
 * The single largest surface on the portfolio landing page. Mirrors
 * `design_handoff_caliber_v2/prototype/screens-portfolio.js → engagementRow()`
 * — a 10-column table where each row tells a partner the most important
 * facts about an engagement at a glance.
 *
 * Visual contract: paper-0 card shell, paper-100 header strip, ink-100 row
 * separators, hover bg = ink-100. Mono numerics on the Go-Live, budget,
 * and last-updated columns; tabular nums via `.num` so columns align.
 *
 * Data sources — every column is wired to a real backend read; no mocks:
 *   - col 1-2: Engagement composite (T-0081 GET /api/v1/portfolio)
 *   - col 3,5,6: T-0119 hero DTO per row (GET /api/v1/engagements/:id)
 *   - col 4:    EngagementDto.planned_end (T-0128)
 *   - col 7:    open_issues_count + critical/high finding count (composite)
 *   - col 8:    useUsersLookupQuery against detail.recent_signals user_ids
 *               — when no team_member_user_ids endpoint is firmwide-visible,
 *               we surface a placeholder dash. The clients table owns the
 *               actual team-member roster.
 *   - col 9:    T-0088 readiness/trend fan-out per row
 *   - col 10:   composite.last_activity_at (relative time)
 */

export interface PortfolioEngagementsTableRow {
  row: PortfolioEngagementDto;
  detail: EngagementDetailDto | undefined;
  detailLoading: boolean;
  trend: ReadinessTrendResult | undefined;
  trendLoading: boolean;
  /** Resolved team members (deterministically sliced to partner + consultant slots). */
  team: ReadonlyArray<TeamMember>;
  /** Optional override of the engagement.planned_end if backend hasn't backfilled. */
  goLive: string | null;
}

export interface PortfolioEngagementsTableProps {
  rows: ReadonlyArray<PortfolioEngagementsTableRow>;
  /** Filter tab strip rendered above the table. */
  tabs: ReadonlyArray<{
    id: string;
    label: string;
    count: number;
    accent?: 'critical' | 'amber';
  }>;
  activeTabId: string;
  onTabChange: (id: string) => void;
  /** "As of" header strip — MAX(last_activity_at) across the set. */
  asOf: string | null;
}

const POSTURE_STROKE: Record<PostureStatus, string> = {
  healthy: 'var(--posture-healthy)',
  'at-risk': 'var(--posture-at-risk)',
  critical: 'var(--posture-critical)',
  unassessed: 'var(--ink-500)',
};

const POSTURE_DOT: Record<PostureStatus, string> = {
  healthy: 'bg-posture-healthy',
  'at-risk': 'bg-posture-at-risk',
  critical: 'bg-posture-critical',
  unassessed: 'bg-posture-unassessed',
};

export function PortfolioEngagementsTable({
  rows,
  tabs,
  activeTabId,
  onTabChange,
  asOf,
}: PortfolioEngagementsTableProps) {
  return (
    <section
      className="overflow-hidden rounded-card border border-ink-100 bg-paper-0"
      aria-label="Engagement list"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-ink-100 px-5 py-4">
        <div className="flex flex-wrap items-center gap-2">
          {tabs.map((tab) => {
            const active = tab.id === activeTabId;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => onTabChange(tab.id)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-none border-b-2 px-2 pb-1.5 text-body-sm font-medium transition-colors',
                  active
                    ? 'border-accent-amber-600 text-ink-900'
                    : 'border-transparent text-ink-700 hover:text-ink-900',
                )}
                aria-pressed={active}
              >
                {tab.label}
                <span
                  className={cn(
                    'font-mono text-meta',
                    tab.accent === 'critical'
                      ? 'text-posture-critical'
                      : tab.accent === 'amber'
                        ? 'text-accent-amber-700'
                        : 'text-ink-500',
                  )}
                >
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>
        <span className="inline-flex h-[22px] items-center gap-1.5 whitespace-nowrap rounded-full bg-ink-100 px-2.5 text-meta font-medium text-posture-unassessed">
          <Clock className="h-3 w-3" aria-hidden="true" />
          {asOf ? `As of ${formatDate(asOf)}` : 'As of —'}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-body-sm">
          <thead>
            <tr className="bg-paper-100">
              <Th width="30%">Engagement</Th>
              <Th>Posture</Th>
              <Th>Phase</Th>
              <Th>Go-Live</Th>
              <Th align="right">Budget used</Th>
              <Th align="right">Open / Overdue</Th>
              <Th align="right">AI proposals</Th>
              <Th>Team</Th>
              <Th>30-day signal</Th>
              <Th align="right">Updated</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ row, detail, detailLoading, trend, trendLoading, team, goLive }) => {
              const postureStatus: PostureStatus = rollupColorToPostureStatus(row.posture);
              const trendPoints = (trend?.points ?? [])
                .map((p) => p.total_percent)
                .filter((v): v is number => v !== null);
              const goLiveMeta = relativeDays(goLive);
              const overdue = row.overdue_deliverable_count;
              // High+critical findings = the "red-flag risks" the prototype
              // surfaces. The composite carries the per-severity breakdown
              // directly; no derivation needed.
              const redFlagOpen =
                row.finding_counts_by_severity.critical + row.finding_counts_by_severity.high;
              const aiProposalCount = detail?.recent_signals.filter(
                (s) => s.kind === 'proposal',
              ).length;
              const phase = detail?.phase_path.find((step) => step.status === 'current')?.label;
              const budget = detail?.budget_rollup;
              return (
                <tr
                  key={row.id}
                  className="border-b border-ink-100 transition-colors last:border-b-0 hover:bg-ink-100/40"
                >
                  <td className="px-3 py-2 align-middle">
                    <Link
                      to="/engagements/$engagementId"
                      params={{ engagementId: row.id }}
                      className="flex items-center gap-2.5 text-ink-700 no-underline hover:text-ink-900"
                    >
                      <span
                        aria-hidden="true"
                        className={cn(
                          'inline-block h-2 w-2 shrink-0 rounded-full',
                          POSTURE_DOT[postureStatus],
                        )}
                      />
                      <span className="min-w-0">
                        <span className="block truncate text-body-sm font-semibold text-ink-900">
                          {row.client_name}
                        </span>
                        <span className="block truncate text-meta text-ink-500">{row.name}</span>
                      </span>
                    </Link>
                  </td>
                  <td className="px-3 py-2 align-middle">
                    <PostureChip status={postureStatus} />
                  </td>
                  <td className="px-3 py-2 align-middle">
                    {detailLoading ? (
                      <span className="inline-block h-3 w-20 animate-pulse rounded bg-ink-100" />
                    ) : phase ? (
                      <span className="text-body-sm text-ink-700">{phase}</span>
                    ) : (
                      <span className="text-meta text-ink-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 align-middle">
                    {goLive ? (
                      <>
                        <div className="font-mono text-body-sm text-ink-900 tabular-nums">
                          {formatDate(goLive)}
                        </div>
                        <div className="text-meta text-ink-500">{goLiveMeta}</div>
                      </>
                    ) : (
                      <span className="text-meta text-ink-300">Not set</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right align-middle tabular-nums">
                    {detailLoading ? (
                      <span className="inline-block h-3 w-12 animate-pulse rounded bg-ink-100" />
                    ) : budget ? (
                      <>
                        <div className="font-semibold text-ink-900">
                          {Math.round((budget.ac / Math.max(budget.bac, 1)) * 100)}%
                        </div>
                        <div
                          className={cn(
                            'text-meta',
                            budget.variance_state === 'critical'
                              ? 'text-posture-critical'
                              : budget.variance_state === 'warn'
                                ? 'text-posture-at-risk'
                                : 'text-ink-500',
                          )}
                        >
                          CPI {budget.cpi.toFixed(2)}
                        </div>
                      </>
                    ) : (
                      <span className="text-meta text-ink-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right align-middle tabular-nums">
                    <div className="font-medium text-ink-700">{row.open_finding_count}</div>
                    <div className="text-meta">
                      <span
                        className={
                          overdue > 0 ? 'font-semibold text-posture-critical' : 'text-ink-300'
                        }
                      >
                        {overdue}
                      </span>{' '}
                      <span className="text-ink-500">overdue</span>
                      {redFlagOpen > 0 ? (
                        <>
                          {' '}
                          <span
                            className="font-semibold text-posture-critical"
                            title="Critical + High severity findings"
                          >
                            · {redFlagOpen} red-flag
                          </span>
                        </>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right align-middle">
                    {detailLoading ? (
                      <span className="inline-block h-5 w-8 animate-pulse rounded bg-ink-100" />
                    ) : aiProposalCount && aiProposalCount > 0 ? (
                      <span className="inline-flex h-5 items-center rounded-full border border-ai-surface-border bg-ai-surface px-2 text-[11px] font-medium text-ai-ink">
                        {aiProposalCount}
                      </span>
                    ) : (
                      <span className="text-meta text-ink-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 align-middle">
                    {team.length === 0 ? (
                      <span className="text-meta text-ink-300">—</span>
                    ) : (
                      <TeamAvatars members={team} max={2} size={24} />
                    )}
                  </td>
                  <td className="px-3 py-2 align-middle">
                    {trendLoading ? (
                      <div className="h-6 w-20 animate-pulse rounded bg-ink-100" />
                    ) : (
                      <Sparkline
                        values={trendPoints}
                        width={80}
                        height={24}
                        stroke={POSTURE_STROKE[postureStatus]}
                      />
                    )}
                  </td>
                  <td className="px-3 py-2 text-right align-middle">
                    <span className="font-mono text-meta text-ink-500">
                      {formatRelative(row.last_activity_at)}
                    </span>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-5 py-10 text-center text-meta text-ink-500">
                  No engagements match this filter.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Th({
  children,
  align,
  width,
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
  width?: string;
}) {
  return (
    <th
      scope="col"
      style={width ? { width } : undefined}
      className={cn(
        'border-b border-ink-100 px-3 py-2 text-meta font-medium text-ink-500',
        align === 'right' ? 'text-right' : 'text-left',
      )}
    >
      {children}
    </th>
  );
}

function relativeDays(iso: string | null): string {
  if (!iso) return '';
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return '';
  const days = Math.round((target - Date.now()) / 86_400_000);
  if (days === 0) return 'today';
  if (days > 0) return `in ${days}d`;
  return `${-days}d ago`;
}

function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return formatDate(iso);
}

PortfolioEngagementsTable.displayName = 'PortfolioEngagementsTable';
